import React, { useMemo } from 'react';
import Select from 'react-select';
import classnames from 'classnames';
import { isNull, isUndefined } from 'lodash/fp';

import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { customStyles, DropdownOptions } from '../../../shared/dropdown/Dropdown';
import { CustomTooltip, OverflowTooltip } from '../../../shared/tooltip';
import styles from './Fields.module.scss';
import { getShowUpdatedOnly } from '../store';
import { useAppSelector } from '../../../../hooks/react-redux';
import { ReadOnlyBlacklineField } from './ReadOnlyBlacklineField';

interface MetaFieldProps {
    id: string;
    value: string[] | null | undefined;
    previousValue?: string | null;
    width: string;
    isLastField?: boolean;
    label: string;
    maxWidth?: string;
    dropdownWidth?: string;
    formatter?: (value: string) => string;
}

export const MetaField: React.FC<MetaFieldProps> = ({ id, value, previousValue, width, isLastField = false, label, maxWidth = '400px', dropdownWidth = '100%', formatter = capitaliseStartLetter }) => {
    const mappedValue: DropdownOptions = value?.map(value => ({ value, label: formatter(value) }));
    const showUpdatedOnly = useAppSelector(getShowUpdatedOnly);
    const isMulti = useMemo(() => !!value && value.length > 1, [value]);

    const dropdownTooltip = useMemo(() => {
        if (!value) {
            return null;
        }
        return value[0];
    }, [value]);

    const metaFieldContent = useMemo(() => {
        if (showUpdatedOnly && !isNull(previousValue) && !isUndefined(previousValue)) {
            const readOnlyValue = value && value[0] || '';
            return (
                <ReadOnlyBlacklineField
                    id={id}
                    value={readOnlyValue}
                    previousValue={previousValue}
                />
            );
        }
        return (
            <CustomTooltip overlayText={dropdownTooltip} trigger='click'>
                <div
                    data-testid={`opinion-meta-${id}`}
                    className={styles.dropdownFieldWrapper}
                    style={{ maxWidth, width: dropdownWidth }}
                >
                    <Select
                        className={styles.dropdownField}
                        classNamePrefix='ark-dropdown'
                        isDisabled
                        value={mappedValue}
                        isMulti={isMulti}
                        styles={customStyles}
                        isClearable={false}
                        hideIndicator
                        minControlHeight='38px'
                        fontWeight={500}
                    />
                </div>
            </CustomTooltip>
        );
    }, [showUpdatedOnly, previousValue, value, id, maxWidth, dropdownWidth, mappedValue, isMulti, dropdownTooltip]);

    return (
        <div
            className={classnames(styles.singleFieldWrapper, { [styles.lastFieldInGroup]: isLastField })}
            style={{ width }}
            data-testid={`opinion-field-${id}-wrapper`}
        >
            <div className={styles.fieldTitleWrapper}>
                <OverflowTooltip
                    className={styles.fieldLabel}
                    overlayText={label}
                    testId={`opinion-field-${id}-label`}
                />
            </div>
            {metaFieldContent}
        </div>
    );
};
